import type {ForwardedRef, ReactElement} from 'react';
import React from 'react';
import type { BottomBarItemProps} from '@Components/contextual-menu/components/bottom-bar-item';
import {BottomBarItem} from '@Components/contextual-menu/components/bottom-bar-item';
import useWindowSize from '@Hooks/useWindowSize';
import styles from './bottom-bar.module.scss';

interface BottomBarProps {
  id: string;
  className?: string;
  items: Array<BottomBarItemProps>;
  onClick(itemId: string): void;
  showSkeleton?: boolean;
  isForEditorBottomBar?: boolean;
}

function BottomBar({isForEditorBottomBar = true, className = '', showSkeleton = false, ...props}: BottomBarProps, ref: ForwardedRef<HTMLDivElement>): ReactElement {
  const {windowWidth} = useWindowSize();

  const onClickHandler = (itemId: string): void => {
    props.onClick(itemId);
  };

  const prepareItems = (): Array<ReactElement> => {
    return props.items.map((item) => {
      return (
        <BottomBarItem
          showSkeleton={showSkeleton}
          key={item.id}
          {...item}
          onClick={(e): void => {
            if (item.onClick) {
              item.onClick(e);
            }
            onClickHandler(item.id);
          }}
        />
      );
    });
  };

  const getGapValue = (): string | undefined => {
    if (!isForEditorBottomBar) {
      return undefined;
    }
    if (windowWidth < 370 || windowWidth > 430) {
      return undefined;
    }

    let gap = 12;

    switch (true) {
      case windowWidth < 380:
        gap = 14;
        break;
      case windowWidth < 390:
        gap = 16;
        break;
      case windowWidth < 400:
        gap = 2;
        break;
      case windowWidth < 410:
        gap = 4;
        break;
      case windowWidth < 420:
        gap = 6;
        break;
      case windowWidth < 430:
        gap = 8;
        break;
      case windowWidth < 440:
        gap = 10;
        break;
      default:
        break;
    }

    return `${gap}px`;
  };

  return (
    <div
      style={{
        gap: getGapValue(),
      }}
      ref={ref}
      className={`flex-h-row ${className} ${styles.options} spacing-p-l-3 spacing-p-r-3`}
    >
      {prepareItems()}
    </div>
  );
}

export default React.forwardRef(BottomBar);
